import { useContext, useEffect, useMemo, useState } from "react";
import client from "../../../../api/client";
import { GlobalContext } from "../../../../context";
import PointsRules, { SOURCES } from "../PointsRules";
import { Avatar, useScopes } from "./staffUi";

const ago = (d) => {
  if (!d) return "No activity yet";
  const days = Math.round((Date.now() - new Date(`${d}T12:00:00Z`).getTime()) / 86400000);
  return days <= 0 ? "Today" : days === 1 ? "Yesterday" : `${days} days ago`;
};

/** Cohorts from the cohort-course scopes, newest first, each with its courses. */
export const cohortsOf = (scopes) => {
  const map = new Map();
  (scopes || []).forEach((s) => {
    if (!map.has(s.cohort_id)) map.set(s.cohort_id, { id: s.cohort_id, name: s.cohort, courses: [] });
    map.get(s.cohort_id).courses.push({ id: s.course_id, title: s.course, students: s.students });
  });
  return [...map.values()];
};

/**
 * Leaderboard, cohort by cohort. Students see their own course's board, so staff pick a course
 * (or all courses in the cohort, to compare). Ranked by total points.
 */
export default function StaffLeaderboardPage() {
  const { authState } = useContext(GlobalContext);
  const isAdmin = !!authState?.data?.user?.is_admin;
  const { scopes, scope, failed } = useScopes();
  const cohorts = useMemo(() => cohortsOf(scopes), [scopes]);
  const [cohortId, setCohortId] = useState(null);
  const [courseId, setCourseId] = useState(null); // null = all courses in the cohort
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => { if (scope && cohortId === null) { setCohortId(scope.cohort_id); setCourseId(scope.course_id); } }, [scope, cohortId]);
  const cohort = cohorts.find((c) => c.id === cohortId);

  useEffect(() => {
    if (!cohortId) return;
    setData(null); setError(false);
    client.get("/staff/leaderboard", { params: { cohort_id: cohortId, ...(courseId ? { course_id: courseId } : {}) } })
      .then((r) => setData(r.data)).catch(() => setError(true));
  }, [cohortId, courseId]);

  const rows = (data?.entries || []).filter((e) => `${e.name} ${e.email}`.toLowerCase().includes(q.toLowerCase()));
  const all = !courseId;

  return (
    <div className="lms"><div className="main single" style={{ maxWidth: 1240 }}><div className="view">
      <div className="sp-top">
        <div><span className="sp-kicker">{isAdmin ? "Learning" : "Teach"}</span><h1>Leaderboard</h1>
          <p>Students see the board for their own course. Points come from classes, quizzes, assignments, projects and the daily challenge.</p></div>
        {cohorts.length > 1 && (
          <div className="sp-filters">
            <label className="sp-field" htmlFor="lb-cohort">Cohort
              <select id="lb-cohort" value={cohortId || ""} onChange={(e) => { setCohortId(+e.target.value); setCourseId(null); }}>
                {cohorts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          </div>
        )}
      </div>

      {cohort && (
        <div className="lb-tabs chips-row" role="group" aria-label="Course" style={{ marginBottom: 14, flexWrap: "wrap" }}>
          {cohort.courses.length > 1 && <button type="button" aria-pressed={all} onClick={() => setCourseId(null)}>All courses</button>}
          {cohort.courses.map((c) => <button key={c.id} type="button" aria-pressed={courseId === c.id || (cohort.courses.length === 1)} onClick={() => setCourseId(c.id)}>{c.title} · {c.students}</button>)}
        </div>
      )}

      {failed ? <section className="complete-box"><div><strong>We couldn't load your courses</strong><small>Refresh the page to try again.</small></div></section>
        : scopes && !scopes.length ? <section className="complete-box"><div><strong>No courses to show</strong><small>Courses appear here once they're in a cohort.</small></div></section>
        : error ? <section className="complete-box"><div><strong>We couldn't load the leaderboard</strong><small>Check your connection and try again.</small></div></section>
        : !data ? <div className="skeleton" style={{ height: 320 }} />
        : (
          <>
            <div className="sp-mini" style={{ marginBottom: 14 }}>
              <div><b>{data.entries.length}</b><span>Students on this board</span></div>
              <div><b>{data.entries[0]?.points ?? 0}</b><span>Top score{data.entries[0] ? ` · ${data.entries[0].name.split(" ")[0]}` : ""}</span></div>
              <div><b>{data.points_this_week}</b><span>Points earned in the last 7 days</span></div>
              <div><b>{data.entries.filter((e) => e.points === 0).length}</b><span>Still on 0 points</span></div>
            </div>
            <section className="sp-box">
              <div className="sp-box-h"><h2>{all ? `All courses · ${data.cohort.name}` : data.course?.title}</h2>
                <input type="search" className="sp-select" placeholder="Search name or email" aria-label="Search students" value={q} onChange={(e) => setQ(e.target.value)} /></div>
              <div className="sp-table-wrap"><table className="sp-table">
                <thead><tr><th className="num">Rank</th><th>Student</th>{all && <th>Course</th>}<th className="num">Points</th><th className="num">Last 7 days</th><th className="num">Streak</th><th>Last active</th></tr></thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id}>
                      <td className="num"><span className={`sp-rank r${e.rank <= 3 ? e.rank : ""}`}>{e.rank}</span></td>
                      <td><div className="sp-stu"><Avatar s={e} /><div><strong>{e.name}</strong><small>{e.email}</small></div></div></td>
                      {all && <td className="course-tag" style={{ fontSize: 13 }}>{e.courses.join(", ")}</td>}
                      <td className="num" title={SOURCES.map(([k, l]) => `${l}: ${e.breakdown?.[k] ?? 0}`).join(" · ")}><b style={{ color: "var(--ink)" }}>{e.points}</b>{e.points > 0 && <div className="course-tag" style={{ fontSize: 11.5, whiteSpace: "nowrap" }}>{SOURCES.filter(([k]) => e.breakdown?.[k]).map(([k, l]) => `${l.split(" ")[0]} ${e.breakdown[k]}`).join(" · ")}</div>}</td>
                      <td className="num" style={{ color: e.week ? "var(--good)" : "var(--muted)" }}>{e.week ? `+${e.week}` : "0"}</td>
                      <td className="num">{e.streak ? `${e.streak} day${e.streak === 1 ? "" : "s"}` : "—"}</td>
                      <td style={{ color: "var(--muted)" }}>{ago(e.last_active)}</td>
                    </tr>
                  ))}
                  {!rows.length && <tr><td colSpan={all ? 7 : 6} className="course-tag">{q ? "No students match." : "No students on this board yet."}</td></tr>}
                </tbody>
              </table></div>
              {all && <div className="sp-foot"><span>Students taking two courses appear once, with both courses listed. Points are what they've earned in this cohort.</span></div>}
            </section>
            <PointsRules rules={data.rules} title="How students earn points" />
          </>
        )}
    </div></div></div>
  );
}
